<template>
  <div class="pp" role="group" :aria-label="t('player.controls.playlist')">
    <div v-if="items.length > 0" class="pp__items">
      <button
        v-for="(item, index) in items"
        :key="item.asset.uid"
        class="pp__item"
        :class="{ 'pp__item--active': currentIndex === index }"
        :aria-current="currentIndex === index ? 'true' : undefined"
        @click="emit('select', index)"
      >
        <!-- Thumbnail -->
        <div class="pp__thumbnail">
          <img
            v-if="item.asset.thumbnailUrl"
            :src="item.asset.thumbnailUrl"
            :alt="item.customTitle || item.asset.name"
            class="pp__thumbnail-img"
            loading="lazy"
          />
          <div v-else class="pp__thumbnail-placeholder">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
            </svg>
          </div>
          <span class="pp__index" aria-hidden="true">{{ index + 1 }}</span>
          <span v-if="itemDuration(item)" class="pp__duration">{{ formatTime(itemDuration(item)) }}</span>
          <!-- Now playing indicator -->
          <div v-if="currentIndex === index && isPlaying" class="pp__now-playing" aria-hidden="true">
            <span class="pp__eq-bar" />
            <span class="pp__eq-bar" />
            <span class="pp__eq-bar" />
          </div>
        </div>

        <!-- Content -->
        <div class="pp__content">
          <span class="pp__title">{{ item.customTitle || item.asset.name }}</span>
        </div>
      </button>
    </div>
    <div v-else class="pp__placeholder">
      {{ t('player.panels.playlist.empty') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AssetListItem } from '../../../types/asset'
import type { CollectionItem } from '../../../types/player'
import { useVideoI18n } from '../../i18n'
import { formatTime } from '../../utils/time'

/**
 * Playlist panel — pure presentation over a list of collection items.
 *
 * Kept free of any player instance so the collection shell can render the very
 * same panel around a third-party item, where no video player exists.
 */
interface Props {
  items: CollectionItem<AssetListItem>[]
  currentIndex: number
  isPlaying?: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  select: [index: number]
}>()

const { t } = useVideoI18n()

// Only timed assets (video, audio, third-party) carry a duration.
function itemDuration(item: CollectionItem<AssetListItem>): number {
  return (item.asset as { duration?: number }).duration ?? 0
}
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.pp {
  padding: 8px 0;
  overflow-x: hidden;

  &__items {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 10px;
    overflow-y: auto;
    @include custom-scrollbar;
  }

  // ── Item ──
  &__item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 10px;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: 8px;
    text-align: left;
    font-family: inherit;
    width: 100%;
    color: inherit;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }

    &--active {
      background-color: var(--vp-surface-hover);

      .pp__title {
        color: var(--vp-text-primary);
      }
    }
  }

  // ── Thumbnail ──
  &__thumbnail {
    position: relative;
    width: 100px;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    overflow: hidden;
    background-color: var(--vp-surface-subtle);
    flex-shrink: 0;
  }

  &__thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--vp-surface-hover);
    color: var(--vp-text-muted);

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__duration {
    position: absolute;
    bottom: 2px;
    right: 2px;
    background-color: rgba(0, 0, 0, 0.8);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    padding: 1px 4px;
    border-radius: 3px;
    line-height: 1.3;
  }

  // ── Now Playing Equalizer ──
  &__now-playing {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    background-color: rgba(0, 0, 0, 0.35);
  }

  &__eq-bar {
    display: block;
    width: 3px;
    height: 14px;
    background-color: white;
    border-radius: 1px;
    animation: pp-equalizer 0.8s ease-in-out infinite alternate;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  // ── Index badge (on thumbnail, top-left) ──
  &__index {
    position: absolute;
    top: 2px;
    left: 2px;
    font-size: 0.75rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: rgba(255, 255, 255, 0.9);
    min-width: 16px;
    padding: 1px 4px;
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 3px;
    line-height: 1.3;
    text-align: center;
    z-index: 1;
  }

  // ── Content ──
  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
    padding-top: 1px;
  }

  &__title {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-tertiary);
    line-height: 1.4;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.15s ease;
  }

  // ── Empty ──
  &__placeholder {
    padding: 24px 16px;
    text-align: center;
    // text-ghost (0.3 alpha) was ~2:1 in both themes.
    color: var(--vp-text-tertiary);
    font-size: 0.8125rem;
    font-style: italic;
  }
}

@keyframes pp-equalizer {
  0% {
    transform: scaleY(0.3);
  }
  100% {
    transform: scaleY(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pp__item,
  .pp__title {
    transition: none;
  }

  .pp__eq-bar {
    animation: none;
    transform: scaleY(0.7);
  }
}
</style>
